"use client";

import { type ReactNode, useMemo } from "react";
import { Button, Glyph } from "@/components/desktop/ui";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { useClientTranslations } from "@/hooks/useClientTranslations";
interface TranslationStrings {
  title: string;
  failedToLoad: string;
  genericError: string;
  tryAgain: string;
}
interface Props {
  children: ReactNode;
  windowTitle?: string;
  translations: TranslationStrings;
  windowType?: string;
  windowId?: string;
}
export function useWindowErrorBoundaryTranslations(): TranslationStrings {
  const t = useClientTranslations("desktop.errorBoundary");
  return useMemo(() => ({
    title: t("title"),
    failedToLoad: t("failedToLoad"),
    genericError: t("genericError"),
    tryAgain: t("tryAgain")
  }), [t]);
}
function WindowErrorFallback({
  windowTitle,
  translations,
  onRetry
}: {
  windowTitle?: string;
  translations: TranslationStrings;
  onRetry: () => void;
}) {
  const errorMessage = windowTitle ? translations.failedToLoad.replace("%windowTitle%", windowTitle) : translations.genericError;
  return <div className="flex flex-col items-center justify-center h-full p-8 text-center" data-sentry-component="WindowErrorFallback" data-sentry-source-file="WindowErrorBoundary.tsx">
            <span className="mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-tone-red text-tone-red-ink" aria-hidden="true">
                <Glyph name="alert" className="h-5 w-5" data-sentry-element="Glyph" data-sentry-source-file="WindowErrorBoundary.tsx" />
            </span>
            <h3 className="text-lg font-medium text-ink mb-2">
                {translations.title}
            </h3>
            <p className="text-sm text-ink-2 mb-4 max-w-md">{errorMessage}</p>
            <Button variant="secondary" size="sm" onClick={onRetry} data-sentry-element="Button" data-sentry-source-file="WindowErrorBoundary.tsx">
                <Glyph name="refresh" className="h-4 w-4" data-sentry-element="Glyph" data-sentry-source-file="WindowErrorBoundary.tsx" />
                {translations.tryAgain}
            </Button>
        </div>;
}
export function WindowErrorBoundary({
  children,
  windowTitle,
  translations,
  windowType,
  windowId
}: Props) {
  return <ErrorBoundary tags={{
    surface: "window",
    ...(windowType && {
      windowType
    }),
    ...(windowId && {
      windowId
    })
  }} fallback={retry => <WindowErrorFallback windowTitle={windowTitle} translations={translations} onRetry={retry} />} data-sentry-element="ErrorBoundary" data-sentry-component="WindowErrorBoundary" data-sentry-source-file="WindowErrorBoundary.tsx">
            {children}
        </ErrorBoundary>;
}